import { apiService } from './api';
import { GeographicData, AnalyticsEventType } from '../types';
import { getCountryForTimezone } from 'countries-and-timezones';

class AnalyticsTracker {
  private visitorId: string;
  private pageId: string;
  private pageViewTracked: boolean = false;

  constructor(pageId: string) {
    this.pageId = pageId;
    this.visitorId = this.getOrCreateVisitorId();
  }

  private getOrCreateVisitorId(): string {
    const storageKey = `landing_visitor_${this.pageId}`;
    let visitorId = localStorage.getItem(storageKey);

    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(storageKey, visitorId);
    }

    return visitorId;
  }


  private getGeographicData(): GeographicData {
    if (typeof window === 'undefined') {
      return {
        country: 'Unknown',
        region: 'Unknown',
        city: 'Unknown',
        timezone: 'Unknown'
      };
    }

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      if (!timezone) {
        return {
          country: 'Unknown',
          region: 'Unknown',
          city: 'Unknown',
          timezone: 'Unknown'
        };
      }

      // Use countries-and-timezones package for accurate mapping
      const timezoneInfo = getCountryForTimezone(timezone);
      
      // Extract country name, fallback to 'Unknown' if not found
      const country = timezoneInfo?.name || 'Unknown';
      const region = country; // For backward compatibility, region = country

      return {
        country,
        region,
        city: 'Unknown', // Would need IP geolocation for city
        timezone
      };
    } catch (error) {
      console.warn('Failed to get geographic data:', error);
      // Always return a valid structure, never empty object
      return {
        country: 'Unknown',
        region: 'Unknown',
        city: 'Unknown',
        timezone: 'Unknown'
      };
    }
  }


  async trackPageView(): Promise<void> {
    if (this.pageViewTracked) return; // Only track once per page load

    const geographic = this.getGeographicData();

    await this.sendEvent('page_view', {
      url: window.location.href,
      referrer: document.referrer,
      geographic,
      timeOnPage: 0 // Will be updated on session end
    });

    this.pageViewTracked = true;
  }

  async trackButtonClick(buttonId: string, buttonText: string, link: string): Promise<void> {
    // Filter out crawler traffic entirely
    if (this.isCrawlerTraffic(navigator.userAgent)) {
      return;
    }

    sessionStorage.setItem('main_button_clicked', 'true');

    await this.sendEvent('button_click', {
      buttonId,
      buttonText,
      destination: link,
      position: this.getButtonPosition(buttonId)
    });
  }




  // Get button position on page (for UX analysis)
  private getButtonPosition(buttonId: string): { x: number; y: number } | undefined {
    try {
      const button = document.querySelector(`[data-button-id="${buttonId}"]`) as HTMLElement;
      if (button) {
        const rect = button.getBoundingClientRect();
        return {
          x: Math.round(rect.left + rect.width / 2),
          y: Math.round(rect.top + rect.height / 2)
        };
      }
    } catch (error) {
      console.warn('Failed to get button position:', error);
    }
    return undefined;
  }

  // Send event to backend
  private async sendEvent(eventType: AnalyticsEventType, eventData: any): Promise<void> {
    // Filter out crawler traffic entirely
    if (this.isCrawlerTraffic(navigator.userAgent)) {
      return; // Silently ignore crawler analytics
    }

    try {
      await apiService.recordAnalytics(this.pageId, {
        visitorId: this.visitorId,
        eventType,
        eventData,
        userAgent: navigator.userAgent,
        ipAddress: undefined // Will be set by server
      });
    } catch (error) {
      console.error(`Failed to track ${eventType}:`, error);
      // Don't throw - analytics failures shouldn't break the app
    }
  }

  // Detect crawler traffic
  // Improved to distinguish actual crawlers from legitimate app browsers
  private isCrawlerTraffic(userAgent: string): boolean {
    if (!userAgent) return false;

    const crawlerPatterns = [
      /googlebot/i,
      /bingbot/i,
      /yandexbot/i,
      /facebookexternalhit/i,
      /twitterbot/i,
      /linkedinbot/i,
      /slackbot/i,
      /discordbot/i,
      /bot\//i,
      /crawler\//i,
      /spider\//i
    ];

    return crawlerPatterns.some(pattern => pattern.test(userAgent));
  }

  getVisitorInfo() {
    return {
      visitorId: this.visitorId
    };
  }
}

// Singleton instance per page
const analyticsInstances: Map<string, AnalyticsTracker> = new Map();

export const getAnalyticsTracker = (pageId: string): AnalyticsTracker => {
  if (!analyticsInstances.has(pageId)) {
    analyticsInstances.set(pageId, new AnalyticsTracker(pageId));
  }
  return analyticsInstances.get(pageId)!;
};

export { AnalyticsTracker };
